<script setup>
import { computed, onBeforeMount, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import store from '@/lib/store';
import DataPoint from "@/components/views/inputs/DataPoint.vue";
import { useReportExport } from '@/assets/modules/composables/reportExport.js';

/**
 * The County's monthly performance report.
 *
 * Renders the six sections the RFP "Performance Monitoring and Reporting" clause requires.
 * The period lives in the URL rather than in component state so a specific month can be
 * bookmarked and re-opened -- the report is a contractual deliverable, and being able to
 * return to exactly the copy that was sent matters more than a tidier control.
 *
 * Every table comes from the API in the standard `meta.tables` shape, which is what lets the
 * shared export composable produce the CSV and Excel files without any bespoke code here.
 */

const route = useRoute();
const router = useRouter();

const pageData = ref();
const tables = ref([]);
const response = ref();
const loading = ref(false);

const MONTH_OPTIONS = [
    { title: 'January', value: 1 },
    { title: 'February', value: 2 },
    { title: 'March', value: 3 },
    { title: 'April', value: 4 },
    { title: 'May', value: 5 },
    { title: 'June', value: 6 },
    { title: 'July', value: 7 },
    { title: 'August', value: 8 },
    { title: 'September', value: 9 },
    { title: 'October', value: 10 },
    { title: 'November', value: 11 },
    { title: 'December', value: 12 },
];

const selectedYear = ref(Number(route.params.year));
const selectedMonth = ref(Number(route.params.month));

// Ten years back is enough to cover any contract term without turning the control into a
// scrolling exercise.
const yearOptions = computed(() => {
    const currentYear = new Date().getFullYear();
    return Array.from({ length: 10 }, (unused, offset) => currentYear - offset);
});

async function loadReport() {
    loading.value = true;
    try {
        response.value = await store.dispatch("getPerformanceReport", {
            year: route.params.year,
            month: route.params.month,
        });
        if (response.value?.success) {
            pageData.value = response.value.data;
            // No breadcrumb dispatch: the MainPage shell does not carry the OPM store's
            // `setBreadcrumbs` action, and dispatching it here logs an unknown-action error.
            // The endpoint still returns meta.breadcrumbs for any future consumer.
            tables.value = response.value.meta.tables || [];
        }
    } finally {
        loading.value = false;
    }
}

onBeforeMount(loadReport);

// The URL is the single source of truth for the period, so changing the selects navigates and
// the route watcher does the fetching. Fetching directly from the select handler would let the
// address bar and the rendered report drift apart.
function changePeriod() {
    router.push(`/performance-report/${selectedYear.value}/${selectedMonth.value}`);
}

watch(() => [route.params.year, route.params.month], () => {
    selectedYear.value = Number(route.params.year);
    selectedMonth.value = Number(route.params.month);
    loadReport();
});

const { exportXlsx, exportCsv } = useReportExport();

async function downloadXlsx() {
    await exportXlsx(tables.value, exportFilename.value);
}
function downloadCsv() {
    exportCsv(tables.value, exportFilename.value);
}

// Names the period in the file, so a folder of these stays sortable and a reviewer can tell
// two months apart without opening them.
const exportFilename = computed(() => {
    const month = String(route.params.month).padStart(2, '0');
    return `Performance Report ${route.params.year}-${month}`;
});
</script>

<template>
    <v-container fluid>
        <v-row>
            <v-col>
                <h1>Monthly Performance Report</h1>
            </v-col>
            <v-col class='ga-4' cols='auto'>
                <v-menu>
                    <template #activator="{ props }">
                        <v-btn
                            class="mr-4"
                            color="primary"
                            variant="outlined"
                            prepend-icon="mdi-download"
                            append-icon="mdi-menu-down"
                            :disabled="!tables.length"
                            v-bind="props"
                        >
                            Export
                        </v-btn>
                    </template>
                    <v-list>
                        <v-list-item prepend-icon="mdi-microsoft-excel" title="Excel (.xlsx)" @click="downloadXlsx" />
                        <v-list-item prepend-icon="mdi-file-delimited" title="CSV (.csv)" @click="downloadCsv" />
                    </v-list>
                </v-menu>
            </v-col>
        </v-row>

        <v-row>
            <v-col cols="12" sm="3">
                <v-select
                    v-model="selectedMonth"
                    :items="MONTH_OPTIONS"
                    label="Month"
                    density="comfortable"
                    @update:modelValue="changePeriod"
                />
            </v-col>
            <v-col cols="12" sm="3">
                <v-select
                    v-model="selectedYear"
                    :items="yearOptions"
                    label="Year"
                    density="comfortable"
                    @update:modelValue="changePeriod"
                />
            </v-col>
        </v-row>

        <v-row v-if="loading">
            <v-col>
                <v-progress-linear indeterminate color="primary" />
            </v-col>
        </v-row>

        <template v-if="pageData">
            <v-row>
                <v-col>
                    <DataPoint :pageData='pageData' fieldKey='period'/>
                </v-col>
                <v-col>
                    <DataPoint :pageData='pageData' fieldKey='total_requests'/>
                </v-col>
                <v-col>
                    <DataPoint :pageData='pageData' fieldKey='open_requests'/>
                </v-col>
            </v-row>

            <v-row><v-col><v-divider></v-divider></v-col></v-row>

            <v-row v-for="(tableObj, index) in tables" :key="index">
                <v-col class="mt-8">
                    <h3>{{ Object.values(tableObj)[0].title }}</h3>
                    <v-data-table
                        :headers="Object.values(tableObj)[0].tableHeaders"
                        :items="Object.values(tableObj)[0].tableData"
                        :items-per-page="10"
                        disable-sort
                    >
                        <!-- An empty section is a real answer -- no training happened, no risks
                             were identified -- so it is stated rather than left blank. -->
                        <template #no-data>
                            <div class="py-4 text-medium-emphasis">Nothing recorded for this period.</div>
                        </template>
                    </v-data-table>
                </v-col>
            </v-row>
        </template>
    </v-container>
</template>
